import { formatDate, formatDayOfYear, normalizeToLocalNoon } from '@/lib/format-utils';
import { UnreachableCaseStatement } from '@reach/shared-types/unreachable-case-statement';
import { addDays, addMonths, addYears, subDays, subMonths, subYears } from 'date-fns';
import {
  ConditionOperatorEnumType,
  DateUnit,
  RelativeDateDirection,
  relativeDateSchema,
  type ConditionOperator,
  type RelativeDate,
} from '@reach/shared-types/conditions';
import type { BuilderConditionValue } from '../segment-builder.types';
import { isBetweenDate, isRelativeDate } from './date-value-utils';

function resolveDateValue(value: Date | RelativeDate | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return normalizeToLocalNoon(value);

  const parsedRelativeDate = relativeDateSchema.safeParse(value);
  if (!parsedRelativeDate.success) {
    return null;
  }
  const relativeDate = parsedRelativeDate.data;

  const referenceDate = normalizeToLocalNoon(new Date());
  const isAfter = relativeDate.direction === RelativeDateDirection.AFTER;
  let resolvedDate: Date;
  switch (relativeDate.unit) {
    case DateUnit.DAYS:
      resolvedDate = isAfter
        ? addDays(referenceDate, relativeDate.value)
        : subDays(referenceDate, relativeDate.value);
      break;
    case DateUnit.MONTHS:
      resolvedDate = isAfter
        ? addMonths(referenceDate, relativeDate.value)
        : subMonths(referenceDate, relativeDate.value);
      break;
    case DateUnit.YEARS:
      resolvedDate = isAfter
        ? addYears(referenceDate, relativeDate.value)
        : subYears(referenceDate, relativeDate.value);
      break;
    default:
      throw UnreachableCaseStatement(relativeDate.unit, DateUnit);
  }

  return Number.isNaN(resolvedDate.getTime()) ? null : resolvedDate;
}

function formatResolvedDate(date: Date, includeYear: boolean): string {
  return includeYear ? formatDate(date) : formatDayOfYear(date);
}

const SINGLE_DATE_PREVIEW_CONFIG: Partial<
  Record<ConditionOperator, { prefix: string; includeYear: boolean; suffix?: string }>
> = {
  [ConditionOperatorEnumType.EQUALS_MONTH_DAY]: {
    prefix: 'Matches records on:',
    includeYear: false,
    suffix: ' (any year)',
  },
  [ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR]: {
    prefix: 'Matches records on:',
    includeYear: true,
  },
  [ConditionOperatorEnumType.GREATER_THAN]: {
    prefix: 'Matches records after:',
    includeYear: true,
  },
  [ConditionOperatorEnumType.LESS_THAN]: {
    prefix: 'Matches records before:',
    includeYear: true,
  },
};

export function getResolvedDatePreviewText(
  operator: ConditionOperator,
  value: BuilderConditionValue | undefined,
): string | null {
  if (!value) return null;

  const singleDatePreview = SINGLE_DATE_PREVIEW_CONFIG[operator];
  if (singleDatePreview) {
    const resolvedDate = resolveDateValue(
      value instanceof Date || isRelativeDate(value) ? value : undefined,
    );
    if (!resolvedDate) return null;

    return `${singleDatePreview.prefix} ${formatResolvedDate(resolvedDate, singleDatePreview.includeYear)}${singleDatePreview.suffix ?? ''}`;
  }

  switch (operator) {
    case ConditionOperatorEnumType.BETWEEN_MONTH_DAY:
    case ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR: {
      if (!isBetweenDate(value)) return null;

      const startDate = resolveDateValue(value.startDate);
      const endDate = resolveDateValue(value.endDate);

      if (!startDate || !endDate) return null;

      const includeYear = operator === ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR;
      const startLabel = formatResolvedDate(startDate, includeYear);
      const endLabel = formatResolvedDate(endDate, includeYear);

      if (startLabel === endLabel) {
        return includeYear
          ? `Matches records on: ${startLabel}`
          : `Matches records on: ${startLabel} (any year)`;
      }

      return includeYear
        ? `Matches records from ${startLabel} - ${endLabel}`
        : `Matches records from ${startLabel} - ${endLabel} (any year)`;
    }
    default:
      return null;
  }
}
